import { Gem } from 'lucide-react';

import { useNamespace, useTranslation } from '@splitfiesta/i18n';

import { useFeatureFlag } from '@/hooks/queries/useFeatureFlagsQuery';

import { cn } from '@/lib/utils';

interface SupporterBadgeProps {
  className?: string;
  /** Gray instead of orange, for data rows whose value is the only coloured element. */
  muted?: boolean;
}

export function SupporterBadge({
  className,
  muted = false,
}: SupporterBadgeProps) {
  const { t } = useTranslation();
  // Expense detail and the activity feed do not load `members` themselves.
  const ready = useNamespace('members');
  // Gated here so no call site can show a badge with the flag off.
  const proEnabled = useFeatureFlag('pro');
  if (!proEnabled) return null;

  // Until the strings arrive the glyph keeps its place, hidden from screen
  // readers, so the name row does not shift and no raw key is announced.
  const a11y = ready
    ? {
        role: 'img',
        'aria-label': t('members:supporter.a11y'),
        title: t('members:supporter.badge'),
      }
    : { 'aria-hidden': true };

  return (
    <span
      {...a11y}
      data-testid="supporter-badge"
      className={cn(
        'inline-flex shrink-0 items-center align-middle',
        className
      )}
    >
      <Gem
        aria-hidden="true"
        className={cn(
          'h-[0.9em] w-[0.9em]',
          muted
            ? 'text-gray-500 dark:text-gray-400'
            : 'text-orange-600 dark:text-orange-400'
        )}
      />
    </span>
  );
}
